import { Box, FormLabel, HStack, IconButton, Input } from "@chakra-ui/react";
import React from "react";
import { DeleteIcon, PinFilledIcon, PinIcon } from "../../styles/icons";
import Select from "react-select";

interface AdvancedFilterInputProps {
  filterId: string;
  label: string;
  value: any;
  onChange: (value: any) => void;
  isPinned: boolean;
  onTogglePin: () => void;
  onRemove: () => void;
  options?: { Key?: string; Value?: unknown }[];
}

export const AdvancedFilterInput: React.FC<AdvancedFilterInputProps> = ({
  filterId,
  label,
  value,
  onChange,
  isPinned,
  onTogglePin,
  onRemove,
  options,
}) => {
  const renderInput = () => {
    switch (filterId) {
      case "financial":
        const financialOptions = [
          { label: "None", value: "" },
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ];
        const financialValue =
          financialOptions.find((o) => o.value === (value || "")) ??
          financialOptions[0];
        return (
          <Select
            name="advanced-filter-financial"
            value={financialValue}
            onChange={(selected: any) =>
              onChange(selected ? selected.value : "")
            }
            options={financialOptions}
            isClearable={false}
            className="chakra-react-select"
            classNamePrefix="chakra-react-select"
            styles={{
              control: (base) => ({
                ...base,
                fontSize: "14px",
                minHeight: "32px",
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused ? "#DEEBFF" : "white",
                color: "inherit",
              }),
            }}
          />
        );

      case "status":
      case "birthMonth":
      case "gender":
      case "memberLevel":
      case "memberLevelDescription":
      case "membershipType":
      case "membershipTypeDescription":
        // Multi-select for these filters
        const selectOptions =
          options?.map((opt) => ({
            label: opt.Key || String(opt.Value || "") || "",
            value: String(opt.Value || "") || "",
          })) || [];

        const selectedValues = Array.isArray(value)
          ? value
          : value
          ? [value]
          : [];
        const selectedOptions = selectedValues.map((v) => ({
          label: selectOptions.find((o) => o.value === v)?.label || v,
          value: v,
        }));

        return (
          <Select
            isMulti
            name={`advanced-filter-${filterId}`}
            value={selectedOptions}
            onChange={(newValue: any) => {
              const values = newValue ? newValue.map((v: any) => v.value) : [];
              onChange(values);
            }}
            options={selectOptions}
            className="chakra-react-select"
            classNamePrefix="chakra-react-select"
            styles={{
              control: (base) => ({
                ...base,
                fontSize: "12px",
                minHeight: "32px",
              }),
            }}
          />
        );

      case "pointsBalanceEqualTo":
      case "pointsBalanceNotEqualTo":
      case "pointsBalanceGreaterThan":
      case "pointsBalanceLessThan":
      case "pointsBalanceGreaterThanOrEqualTo":
      case "pointsBalanceLessThanOrEqualTo":
        return (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            fontSize="xs"
            placeholder={label}
          />
        );

      default:
        // Text input for unknown types
        return (
          <Input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            fontSize="xs"
            placeholder={label}
          />
        );
    }
  };

  return (
    <Box width="100%" minWidth="200px" maxWidth="300px" position="relative">
      <HStack justify="space-between" mb="1">
        <FormLabel mb="0">{label}</FormLabel>
        <HStack spacing="1">
          <IconButton
            icon={isPinned ? <PinFilledIcon /> : <PinIcon />}
            size="xs"
            variant="ghost"
            aria-label="Pin filter"
            onClick={onTogglePin}
          />
          {!isPinned && (
            <IconButton
              icon={<DeleteIcon />}
              size="xs"
              variant="ghost"
              aria-label="Remove filter"
              onClick={onRemove}
            />
          )}
        </HStack>
      </HStack>
      {renderInput()}
    </Box>
  );
};
